<template>
  <component
    :is="wrapperLink ? CustomNuxtLink : 'div'"
    data-component="GrowArticleCard"
    :data-variant="variant"
    :data-theme="resolvedTheme"
    :to="wrapperLink"
    :target="link?.openInNewTab ? '_blank' : undefined"
    :class="[
      'block rounded-2xl overflow-hidden @container h-full bg-theme-100',
      textClass,
      { 'group cursor-pointer transition-colors duration-300 hover:bg-theme-50': wrapperLink },
    ]">
    <div class="flex h-full" :class="resolvedConfig.wrapper">
      <div v-if="image" :class="resolvedConfig.media">
        <div class="overflow-hidden w-full rounded-2xl" :class="resolvedConfig.mediaWrapper">
          <Image
            :image="image"
            fit="cover"
            class="w-full h-full transition-transform duration-500"
            sizes="xs:100vw sm:100vw md:50vw lg:33vw xl:33vw 2xl:33vw"
            loading="lazy" />
        </div>
      </div>

      <div class="flex flex-col justify-between min-w-0" :class="resolvedConfig.content">
        <div class="flex flex-col" :class="resolvedConfig.textAlignClass">
          <Typography
            as="h3"
            :variant="resolvedConfig.titleVariant"
            :class="[
              resolvedConfig.titleClass,
              textClass,
              { 'card-title': variant === 'standard', 'card-title-narrow': variant === 'narrow' },
            ]"
            class="wrap-break-word">
            {{ title }}
          </Typography>

          <Typography
            v-if="shortDescription"
            as="p"
            variant="body"
            :class="[resolvedConfig.descriptionClass, mutedTextClass]"
            class="mt-6">
            {{ shortDescription }}
          </Typography>
        </div>
        <ul v-if="tags?.length" class="flex flex-wrap gap-2 mt-6" :class="resolvedConfig.tagsAlignClass">
          <li v-for="(tag, i) in tags" :key="`${tag.url ?? tag.title}-${i}`" class="min-w-0 max-w-full">
            <GrowArticleTag :title="tag.title" />
          </li>
        </ul>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { ImageApiModel, LinkApiModel } from '@gro-up/api/corporate'
import type { Domain } from '@gro-up/types'
import { Typography, Image, CustomNuxtLink, GrowArticleTag } from '@gro-up/ui/primitives'
import { computed, inject, type Ref } from 'vue'

export type GrowArticleTagData = {
  title: string
  url?: string | null
}

export type GrowArticleVariant = 'standard' | 'narrow'

/**
 * Card background theme. The five domains carry a full colour scale; the three support accents
 * (green/oker/terra) only expose a `-100` shade. `default` is the neutral cream fallback used when no
 * backend colour is set. All are remapped onto `--color-theme-*` via `[data-theme=…]` in main.css.
 */
export type GrowArticleTheme = Domain | 'support-green' | 'support-oker' | 'support-terra' | 'default'

export type GrowArticleCardProps = {
  title: string
  shortDescription?: string
  image?: ImageApiModel
  link?: LinkApiModel
  tags?: GrowArticleTagData[]
  /** Size family. The container query handles the layout within each family. Default 'standard'. */
  variant?: GrowArticleVariant
  /** Background theme. When omitted, falls back to the injected page domain, then the neutral `default`. */
  theme?: GrowArticleTheme
}

const props = withDefaults(defineProps<GrowArticleCardProps>(), {
  shortDescription: undefined,
  image: undefined,
  link: undefined,
  tags: undefined,
  variant: 'standard',
  theme: undefined,
})

const domain = inject<Ref<Domain | null> | undefined>('domain', undefined)

// No theme prop and no injected domain → the neutral `default` theme (never unthemed).
const resolvedTheme = computed<GrowArticleTheme>(() => props.theme ?? domain?.value ?? 'default')

// These lighter backgrounds take dark text; every other theme takes white.
const DARK_TEXT_THEMES = new Set<GrowArticleTheme>(['child-care', 'head-office', 'support-green', 'support-oker', 'default'])
const useDarkText = computed<boolean>(() => DARK_TEXT_THEMES.has(resolvedTheme.value))
const textClass = computed<string>(() => (useDarkText.value ? 'text-primary-100' : 'text-white'))
const mutedTextClass = computed<string>(() => (useDarkText.value ? 'text-primary-100/90' : 'text-white/90'))

const wrapperLink = computed<string | undefined>(() => props.link?.url || undefined)

type CardConfig = {
  wrapper: string
  media: string
  mediaWrapper: string
  content: string
  titleVariant: 'header-2' | 'header-3' | 'header-4'
  titleClass: string
  descriptionClass: string
  textAlignClass: string
  tagsAlignClass: string
}

/*
 * Layout is driven by container queries (@container on the root), NOT the viewport, so a card adapts to the
 * width of the grid column it lands in. Because the DOM is fixed, image/description visibility is toggled
 * with container-query classes rather than v-if.
 *  - standard: uses a custom `@min-[680px]` threshold (below Tailwind's default `@lg` of 512px) because the
 *              horizontal layout (image beside text) needs more room to breathe than @lg allows — under
 *              680px there simply isn't space for it, so it stays stacked: fixed 265px image on top, content
 *              below, auto height. From 680px: fixed-height card, image beside the content (fills it via
 *              flex + items-stretch), so the image never dictates card height.
 *  - narrow:   always a horizontal card at every width — image beside the title, description never shown.
 *              Height is 160px; only the tightest column (width 3, <400px) may grow when a 3-line 14px title
 *              plus wrapping tags overflow, from 400px up it's hard-locked to 160px. Below ~512px the title
 *              runs at 14px and may wrap to 3 lines; from @lg it's `header-4` clamped to 2 lines.
 */
const CONFIG: Record<GrowArticleVariant, CardConfig> = {
  standard: {
    wrapper: 'h-full flex-col @min-[680px]:flex-row @min-[680px]:items-stretch @min-[680px]:max-h-[480px]',
    media: 'w-full @min-[680px]:w-1/2 @min-[680px]:order-2 shrink-0',
    mediaWrapper: 'h-[265px] @min-[680px]:h-full',
    content: 'flex-1 @min-[680px]:w-1/2 @min-[680px]:order-1 px-4 py-6 @min-[680px]:p-8 @min-[680px]:py-10',
    titleVariant: 'header-3',
    // Font-size + the reserved 3-line height live in the scoped `.card-title` rule below (see the specificity
    // note there), not here. Clamp to 3 lines at every width: horizontally it keeps the title box a fixed
    // height so two full-width cards that stack (e.g. a 4-8/8-4) stay equal height instead of following their
    // own title length; the reserve makes shorter titles match too.
    titleClass: 'line-clamp-3',
    descriptionClass: 'hidden @min-[680px]:block',
    textAlignClass: 'text-center @min-[680px]:text-left',
    tagsAlignClass: 'justify-center @min-[680px]:justify-start',
  },
  narrow: {
    // Compact horizontal card (image right, content left). Height is content-driven, NOT a fixed 160px: a
    // card with little content stays short, and one whose tags wrap to a second row grows. The section grid
    // uses items-stretch, so when one card in a row grows the rest stretch to match — equal heights per row
    // without forcing every card taller. The image stretches to the card height (items-stretch here).
    wrapper: 'flex-row items-stretch',
    // Absolutely-fill the image so it never contributes its intrinsic height to the flex row: the CONTENT
    // column drives the card height and the image just covers whatever height results. `relative` on the
    // media column gives the absolute wrapper its positioning context.
    media: 'w-1/3 order-2 shrink-0 relative',
    mediaWrapper: 'absolute inset-0',
    content: 'flex-1 w-2/3 order-1 p-5',
    titleVariant: 'header-4',
    // Small containers (width 3/4, <512px) run at 14px so a third line fits; from @lg (col 6+) back to 2.
    titleClass: 'line-clamp-3 @lg:line-clamp-2',
    // Narrow never shows the description.
    descriptionClass: 'hidden',
    textAlignClass: '',
    tagsAlignClass: '',
  },
}

const resolvedConfig = computed<CardConfig>(() => CONFIG[props.variant])
</script>

<style scoped>
/*
 * `h3.card-title` (element + class) outweighs Typography's single-class `text-header-3` utilities, so the
 * font-size wins deterministically without `!important`, regardless of Tailwind's build order. Queries the
 * `@container` root established on the card root.
 */
h3.card-title {
  font-size: 25px;
  line-height: 30px;
}

@container (min-width: 680px) {
  h3.card-title {
    font-size: 35px;
    line-height: 42px;
    /* Reserve exactly 3 lines (combined with line-clamp-3) so the title box is a fixed height. That keeps the
     * card compact yet makes two full-width cards that stack line up to the same height. */
    min-height: calc(42px * 3);
  }
}

/* Narrow titles drop to 14px below the column-6 threshold (widths 3/4); above it `header-4` takes over. */
@container (max-width: 511.98px) {
  h3.card-title-narrow {
    font-size: 14px;
    line-height: 20px;
  }
}
</style>
